import { useQuery } from '@tanstack/react-query';
import { WMON } from '@ui/constants/tokens';
import { useDataChainId } from '@ui/hooks/datachain';
import { getLVMONRedeemer } from '@ui/services/contract/contract-instances/getLVMONRedeemer';
import { Token } from '@ui/types/token';
import { Address, formatUnits, parseUnits, zeroAddress } from 'viem';
import { usePublicClient } from 'wagmi';

export const useFastRedeemQuote = ({
  inputToken,
  outputToken,
  inputAmount,
}: {
  inputToken?: Token;
  outputToken?: Token;
  inputAmount: string;
}) => {
  const chainId = useDataChainId();
  const publicClient = usePublicClient({ chainId });

  const isEnabled =
    !!inputToken?.address &&
    !!outputToken?.address &&
    !!inputAmount &&
    !!inputToken.decimals &&
    !!outputToken.decimals &&
    parseFloat(inputAmount) > 0;

  return useQuery({
    queryKey: [
      'fast-redeem-quote',
      chainId,
      inputToken?.address,
      outputToken?.address,
      inputAmount,
      !!publicClient,
    ],
    queryFn: async () => {
      if (!publicClient || !inputToken || !outputToken || !inputAmount) {
        return null;
      }

      const lvmonRedeemer = getLVMONRedeemer({
        chainId,
        publicClient: publicClient as any,
      });

      const amountIn = parseUnits(inputAmount, inputToken.decimals);
      if (amountIn === 0n) return null;

      const reserveToken = outputToken.isNative ? WMON(chainId).address : (outputToken.address as Address);

      try {
        // Use WMON address for reserve token if output is native/WMON
        // Assuming the contract expects the reserve token address (WMON) even if we want native out?
        // Or does it handle zeroAddress?
        // Based on typical patterns, let's try passing the output token address directly first.
        // If outputToken is native, its address might be undefined or special.
        // Let's assume we pass the address of the underlying reserve token.
        // If output is ExchangeMON(), it has isNative=true usually.
        // We probably need the WMON address if it's native.
        // For now, let's use outputToken.address but handle Native case if needed.
        // However, standardRedeem/fastRedeem usually work with the Reserve Token which is ERC20.
        
        // Let's assume we need to pass the actual ERC20 address of the reserve.
        // If outputToken is Native, we might need to look up WMON address.
        // But let's start with outputToken.address.
        
        const result = await lvmonRedeemer.read.previewFastRedeem([
            outputToken.address as Address, // reserveToken
            amountIn, // amount
        ]);

        return {
          amountOut: result,
          formattedAmountOut: formatUnits(result, outputToken.decimals),
          bestCandidate: {
            adapter: lvmonRedeemer.address,
            data: '0x' as `0x${string}`,
          },
        };
      } catch (error) {
        console.error('[useFastRedeemQuote] Error fetching quote:', error);
        return null;
      }
    },
    enabled: isEnabled,
    refetchInterval: 10000,
  });
};
